import React, { useState, useEffect } from 'react';
import workouts from './workouts';
// Remove react-beautiful-dnd
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableExercise({ exercise, index, dayIndex, handleRemoveExercise, toggleExpand, handleWorkoutChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: exercise.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: '1rem',
    margin: '0 0 8px 0',
    background: isDragging ? 'rgba(0, 0, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    // border: '1px solid #ccc',
    borderRadius: 4,
    userSelect: 'none',
    boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : undefined,
    cursor: 'default'
  };
  return (
    <div
      className={`exercise${isDragging ? ' dragging' : ''}`}
      ref={setNodeRef}
      onClick={() => toggleExpand(dayIndex, index)}
      style={style}
    >
      {/* Draggable icon (only this is draggable) */}
      <div className="exercise-header">
        <span
        >
        <span
          className="drag-handle"
          style={{ cursor: 'grab', 
            width: '100%',
            fontSize: '2rem', 
            marginRight: 8, userSelect: 'none' }}
          {...listeners}
          {...attributes}
          tabIndex={0}
          aria-label="Drag to reorder"
        >
          ::
        </span>
          <span>
            {
              workouts[exercise.exercise_type]
                ? workouts[exercise.exercise_type].name
                : exercise.name || 'Exercise'
            }
          </span>
        {/* Remove button always visible */}
        <button
          className="remove-exercise-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveExercise(dayIndex, index);
          }}
        >
          X
        </button>


          <span className="chevron" 
          style={{ marginLeft: 'auto', width: '100%' }}>
            {exercise.expanded ? '▲' : '▼'}
          </span>
        </span>
      </div>
      {exercise.expanded && (
        <div className="exercise-details">
          <div>
            <label htmlFor={`exercise-${dayIndex}-${index}`}>Exercise:</label>
            <select
              id={`exercise-${dayIndex}-${index}`}
              className="workout-selector"
              value={exercise.exercise_type}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'exercise_type', e.target.value)}
            >
              {Object.entries(workouts).map(([key, workout]) => (
                <option key={key} value={key}>
                  {workout.name}
                </option>
              ))}
            </select>
            <a 
              href={`/exercisetypes/${exercise.exercise_type}`}
              target="_blank"
              rel="noopener noreferrer"
              className="view-exercise-btn"
            >
              View Exercise
            </a>
          </div>
          <div>
            <label htmlFor={`sets-${dayIndex}-${index}`}>Sets:</label>
            <input
              id={`sets-${dayIndex}-${index}`}
              type="number"
              value={exercise.sets}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'sets', e.target.value)}
              placeholder="Sets"
            />
          </div>
          <div>
            <label htmlFor={`reps-${dayIndex}-${index}`}>Reps:</label>
            <input
              id={`reps-${dayIndex}-${index}`}
              type="number"
              value={exercise.reps}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'reps', e.target.value)}
              placeholder="Reps"
            />
          </div>
          <div>
            <label htmlFor={`weight-value-${dayIndex}-${index}`}>Weight:</label>
            <input
              id={`weight-value-${dayIndex}-${index}`}
              type="number"
              value={exercise.weight.value}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'weight', { value: e.target.value })}
              placeholder="Weight"
            />
            <select
              id={`weight-unit-${dayIndex}-${index}`}
              value={exercise.weight.unit}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'weight', { unit: e.target.value })}
            >
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </select>
          </div>
          <div>
            <label>Tempo:</label>
            <div className="tempo-inputs">
              {exercise.tempo.map((value, tempoIndex) => (
                <input
                  key={tempoIndex}
                  type="number"
                  value={value}
                  onChange={(e) => {
                    const updatedTempo = [...exercise.tempo];
                    updatedTempo[tempoIndex] = parseInt(e.target.value, 10);
                    handleWorkoutChange(dayIndex, index, 'tempo', updatedTempo);
                  }}
                  placeholder={`Tempo ${tempoIndex + 1}`}
                />
              ))}
            </div>
          </div>
          <div>
            <label htmlFor={`notes-${dayIndex}-${index}`}>Notes:</label>
            <textarea
              id={`notes-${dayIndex}-${index}`}
              value={exercise.notes || ''}
              onChange={(e) => handleWorkoutChange(dayIndex, index, 'notes', e.target.value)}
              placeholder="N/A"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const ResultViewAsQueue = ({ workoutProgram, setWorkoutProgram }) => {
  const [loading, setLoading] = useState(true);
  const [progressLevel, setProgressLevel] = useState(10);

  // Move sensors hook here (top-level in the component)
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const interval = setInterval(() => {
      setProgressLevel((prev) => {
        if (prev < 100) {
          return prev + 10;
        } else {
          clearInterval(interval);
          setLoading(false);
          return 100;
        }
      });
    }, 10);
    return () => clearInterval(interval);
  }, []);

  const handleWorkoutChange = (dayIndex, exerciseIndex, field, value) => {
    const updatedProgram = [...workoutProgram];
    if (field === 'tempo') {
      updatedProgram[dayIndex].exercises[exerciseIndex].tempo = value;
    } else if (field === 'weight') {
      updatedProgram[dayIndex].exercises[exerciseIndex].weight = { ...updatedProgram[dayIndex].exercises[exerciseIndex].weight, ...value };
    } else {
      updatedProgram[dayIndex].exercises[exerciseIndex][field] = value;
    }
    setWorkoutProgram(updatedProgram);
  };

  const toggleExpand = (dayIndex, exerciseIndex) => {
    const updatedProgram = [...workoutProgram];
    updatedProgram[dayIndex].exercises[exerciseIndex].expanded = !updatedProgram[dayIndex].exercises[exerciseIndex].expanded;
    setWorkoutProgram(updatedProgram);
  };

  // Remove react-beautiful-dnd onDragEnd
  // const onDragEnd = (result) => { ... }

  // dnd-kit drag end handler for reordering exercises within a day
  const handleDragEnd = (event, dayIndex) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const exercises = workoutProgram[dayIndex].exercises;
    const oldIndex = exercises.findIndex(e => e.id === active.id);
    const newIndex = exercises.findIndex(e => e.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const updatedExercises = arrayMove(exercises, oldIndex, newIndex);
    const updatedProgram = [...workoutProgram];
    updatedProgram[dayIndex] = {
      ...updatedProgram[dayIndex],
      exercises: updatedExercises,
    };
    setWorkoutProgram(updatedProgram);
  };

  // Remove exercise
  const handleRemoveExercise = (dayIndex, exerciseIndex) => {
    const updatedProgram = [...workoutProgram];
    updatedProgram[dayIndex].exercises.splice(exerciseIndex, 1);
    setWorkoutProgram(updatedProgram);
  };

  // Add new exercise
  const handleAddExercise = (dayIndex) => {
    const defaultExerciseKey = Object.keys(workouts)[0];
    // Generate a unique id using timestamp and random
    const uniqueId = `ex-${dayIndex + 1}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    const defaultExercise = {
      id: uniqueId,
      exercise_type: defaultExerciseKey,
      name: defaultExerciseKey,
      sets: 3,
      reps: 10,
      weight: { value: 0, unit: 'kg' },
      tempo: [2, 0, 2, 0],
      notes: '',
      expanded: false,
    };
    const updatedProgram = [...workoutProgram];
    updatedProgram[dayIndex].exercises.push(defaultExercise);
    setWorkoutProgram(updatedProgram);
  };

  if (loading) {
    return (
        <div className="spinnerrrr">
          <progress className="progress" value={progressLevel} max="100">0%</progress>
        </div>);
  }

  return (
    <div className="card exercise-item-card">
      {workoutProgram.map((day, dayIndex) => (
        <div key={dayIndex} className="output">
          <h2>{day.day}</h2>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={event => handleDragEnd(event, dayIndex)}
          >
            <SortableContext
              items={day.exercises.map(e => e.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="exercise-list">
                {day.exercises.map((exercise, exerciseIndex) => (
                  <SortableExercise
                    key={exercise.id}
                    exercise={exercise}
                    index={exerciseIndex}
                    dayIndex={dayIndex}
                    handleRemoveExercise={handleRemoveExercise}
                    toggleExpand={toggleExpand}
                    handleWorkoutChange={handleWorkoutChange}
                  />
                ))}
                {/* Add New Exercise button */}
                <button
                  className="add-exercise-btn"
                  onClick={() => handleAddExercise(dayIndex)}
                  style={{ marginTop: '1em', width: '100%' }}
                >
                  Add New Exercise
                </button>
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ))}
    </div>
  );
};

export default ResultViewAsQueue;